import React, { useState, useEffect } from 'react';
import { 
  fetchPayrollRuns, createPayrollRun,
  lockPayrollRun, reversePayrollRun,
} from '../api';
import StaffConfigurationModal from './StaffConfigurationModal';
import PayrollRunWizard from './PayrollRunWizard';
import PayslipView from './PayslipView';
import { EmployeeList, EmployeeProfile } from './EmployeeList';
import LeaveManagement from './LeaveManagement';
import TaxTableManager from './TaxTableManager';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-200 text-gray-800',
  APPROVED: 'bg-blue-200 text-blue-800',
  POSTED: 'bg-green-200 text-green-800',
  LOCKED: 'bg-purple-200 text-purple-800',
  REVERSED: 'bg-red-200 text-red-800',
};

const SUB_TABS = [
  { key: 'runs', label: 'Payroll Runs' },
  { key: 'employees', label: 'Employees' },
  { key: 'leave', label: 'Leave' },
  { key: 'tax', label: 'Tax Tables' },
];

const PayrollManagement = ({ authAxios, addToast }) => {
  const [subTab, setSubTab] = useState('runs');
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [viewMode, setViewMode] = useState(null); // 'wizard' | 'payslips'
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const [newRunData, setNewRunData] = useState({
    pay_period_start: '',
    pay_period_end: '',
    description: ''
  });

  const loadRuns = async () => {
    setLoading(true);
    try {
      const data = await fetchPayrollRuns(authAxios);
      setRuns(data.results || data);
    } catch (err) {
      addToast('Failed to load payroll runs.', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (subTab === 'runs') loadRuns();
  }, [subTab]);

  const handleCreate = async () => {
    try {
      const data = await createPayrollRun(authAxios, newRunData);
      addToast('Payroll run created successfully', 'success');
      setNewRunData({ pay_period_start: '', pay_period_end: '', description: '' });
      await loadRuns();
      setSelectedRunId(data.id);
      setViewMode('wizard');
    } catch (err) {
      addToast('Failed to create payroll run', 'error');
    }
  };

  // Sub-component views
  if (subTab === 'runs' && viewMode === 'wizard' && selectedRunId) {
    return (
      <div>
        <PayrollRunWizard
          authAxios={authAxios}
          addToast={addToast}
          runId={selectedRunId}
          onClose={() => { setViewMode(null); setSelectedRunId(null); loadRuns(); }}
        />
      </div>
    );
  }

  if (subTab === 'runs' && viewMode === 'payslips' && selectedRunId) {
    return (
      <PayslipView
        authAxios={authAxios}
        addToast={addToast}
        runId={selectedRunId}
        onBack={() => { setViewMode(null); setSelectedRunId(null); }}
      />
    );
  }

  if (subTab === 'employees' && selectedEmployeeId) {
    return (
      <EmployeeProfile
        authAxios={authAxios}
        addToast={addToast}
        profileId={selectedEmployeeId}
        onBack={() => setSelectedEmployeeId(null)}
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="flex justify-between items-center p-6 border-b">
        <h2 className="text-2xl font-bold">Payroll & HR</h2>
        {subTab === 'runs' && (
          <button 
            onClick={() => setShowConfig(true)}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Staff Configuration
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex border-b px-6">
        {SUB_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => { setSubTab(t.key); setSelectedEmployeeId(null); }}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              subTab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* Payroll Runs Tab */}
        {subTab === 'runs' && (
          <>
            <div className="bg-gray-50 p-4 rounded-md mb-6 flex gap-4 items-end">
              <div>
                <label className="block text-sm">Start Date</label>
                <input type="date" className="border rounded p-2" value={newRunData.pay_period_start} onChange={e => setNewRunData({...newRunData, pay_period_start: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm">End Date</label>
                <input type="date" className="border rounded p-2" value={newRunData.pay_period_end} onChange={e => setNewRunData({...newRunData, pay_period_end: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm">Description</label>
                <input type="text" className="border rounded p-2" value={newRunData.description} onChange={e => setNewRunData({...newRunData, description: e.target.value})} />
              </div>
              <button onClick={handleCreate} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Create Run</button>
            </div>

            {loading ? <p>Loading...</p> : (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr>
                    <th className="border-b p-2">Period</th>
                    <th className="border-b p-2">Status</th>
                    <th className="border-b p-2">Gross</th>
                    <th className="border-b p-2">Net Pay</th>
                    <th className="border-b p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => (
                    <tr key={run.id} className="border-b hover:bg-gray-50">
                      <td className="p-2">{run.pay_period_start} — {run.pay_period_end}</td>
                      <td className="p-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[run.status] || 'bg-gray-200'}`}>
                          {run.status}
                        </span>
                      </td>
                      <td className="p-2">P {parseFloat(run.total_gross || 0).toFixed(2)}</td>
                      <td className="p-2">P {parseFloat(run.total_net_pay || 0).toFixed(2)}</td>
                      <td className="p-2 space-x-1">
                        <button
                          onClick={() => { setSelectedRunId(run.id); setViewMode('wizard'); }}
                          className="px-2 py-1 bg-blue-500 text-white rounded text-xs"
                        >
                          Open
                        </button>
                        <button
                          onClick={() => { setSelectedRunId(run.id); setViewMode('payslips'); }}
                          className="px-2 py-1 bg-purple-500 text-white rounded text-xs"
                        >
                          Payslips
                        </button>
                      </td>
                    </tr>
                  ))}
                  {runs.length === 0 && <tr><td colSpan="5" className="p-4 text-center">No runs found.</td></tr>}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* Employees Tab */}
        {subTab === 'employees' && (
          <EmployeeList
            authAxios={authAxios}
            addToast={addToast}
            onSelectEmployee={(id) => setSelectedEmployeeId(id)}
          />
        )}

        {/* Leave Tab */}
        {subTab === 'leave' && (
          <LeaveManagement authAxios={authAxios} addToast={addToast} />
        )}

        {/* Tax Tables Tab */}
        {subTab === 'tax' && (
          <TaxTableManager authAxios={authAxios} addToast={addToast} />
        )}
      </div>
      
      {showConfig && (
        <StaffConfigurationModal authAxios={authAxios} addToast={addToast} onClose={() => setShowConfig(false)} />
      )}
    </div>
  );
};

export default PayrollManagement;
